import { create } from "zustand";

export type EventType =
  | "user.signup"
  | "user.suspended"
  | "user.banned"
  | "breeder.submitted"
  | "breeder.verified"
  | "listing.created"
  | "listing.flagged"
  | "report.created"
  | "ticket.created"
  | "payment.failed"
  | "refund.completed";

export interface AdminNotification {
  id: string;
  type: EventType;
  title: string;
  detail: string;
  href?: string;
  severity: "info" | "success" | "warning" | "danger";
  ts: string;
  read: boolean;
}

interface RealtimeState {
  connected: boolean;
  notifications: AdminNotification[];
  unreadCount: number;
  push: (n: AdminNotification) => void;
  markAllRead: () => void;
  markRead: (id: string) => void;
  clear: () => void;
  setConnected: (connected: boolean) => void;
}

const MAX_NOTIFICATIONS = 50;

export const useRealtimeStore = create<RealtimeState>((set) => ({
  connected: true,
  notifications: [],
  unreadCount: 0,

  push: (n) =>
    set((s) => {
      const notifications = [n, ...s.notifications].slice(0, MAX_NOTIFICATIONS);
      return {
        notifications,
        unreadCount: notifications.filter((x) => !x.read).length,
      };
    }),

  markAllRead: () =>
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    })),

  markRead: (id) =>
    set((s) => {
      const notifications = s.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n,
      );
      return {
        notifications,
        unreadCount: notifications.filter((x) => !x.read).length,
      };
    }),

  clear: () => set({ notifications: [], unreadCount: 0 }),
  setConnected: (connected) => set({ connected }),
}));

/**
 * Lightweight no-op hook replacing background EventSource connections.
 */
export const useRealtimeConnection = (_enabled: boolean) => {
  // Realtime SSE disabled per user requirement
};
